import { ElementRef, inject, Injectable } from "@angular/core";
import pillarbox, { Player } from "@srgssr/pillarbox-web";
import { DialogService, DynamicDialogRef } from "primeng/dynamicdialog";
import { VideoAndTextComponent } from "src/app/common/components/video-and-text/video-and-text.component";
import { HomePageFeature } from "src/app/common/models/home-page.model";

@Injectable({ providedIn: "root" })
export class MultiPillarboxService {
  private readonly players = new Map<string, Player>();
  dialogRef: DynamicDialogRef | undefined;
  private readonly dialogService = inject(DialogService);

  dispose(playerId: string): void {
    this.disposePlayer(playerId);
    this.disposeDialog(playerId);
  }

  disposePlayer(playerId: string): void {
    this.players.get(playerId)?.dispose();
    this.players.delete(playerId);
  }

  disposeDialog(playerId: string): void {
    this.dialogRef?.close();
    this.dialogRef = undefined;
  }

  hasPlayer(playerId: string): boolean {
    return this.players.has(playerId);
  }

  initPlayer(playerId: string, container: ElementRef, audioOnlyMode: boolean): void {
    this.players.set(
      playerId,
      pillarbox(container.nativeElement, {
        audioOnlyMode,
        playbackRates: [0.5, 1, 1.5, 2],
        fluid: true,
        titleBar: false,
      }) as Player,
    );
  }

  initOrSetSource(playerId: string, source: string, container?: ElementRef, audioOnlyMode?: boolean): void {
    if (!this.hasPlayer(playerId) && container && audioOnlyMode !== undefined) {
      this.initPlayer(playerId, container, audioOnlyMode);
    }
    this.setSource(playerId, source);
  }

  getCurrentTime(playerId: string): number {
    return this.players.get(playerId)?.currentTime() ?? 0;
  }

  setCurrentTime(playerId: string, value: number): void {
    this.players.get(playerId)?.currentTime(value);
  }

  paused(playerId: string): boolean {
    return this.players.get(playerId)?.paused() ?? true;
  }

  pause(playerId: string): void {
    this.players.get(playerId)?.pause();
  }

  async play(playerId: string): Promise<void> {
    await this.players.get(playerId)?.play();
  }

  setSource(playerId: string, source: string): void {
    if (source.includes("urn:")) {
      this.players.get(playerId)?.src({ src: source, type: "srgssr/urn" });
    } else {
      this.players.get(playerId)?.src(source);
    }
  }

  openDialogWithVideo(feature: HomePageFeature, videoTime?: number): void {
    this.dialogRef?.close();
    this.dialogRef = this.dialogService.open(VideoAndTextComponent, {
      header: feature.title,
      width: "500px",
      modal: false,
      draggable: true,
      resizable: true,
      closable: true,
      data: {
        feature,
        videoTime,
      },
    });
  }
}
